/**
 * useAltData – Zustand store for Alternative Data intelligence.
 * Fetches all alt data categories grouped into sections.
 */
import { create } from 'zustand'
import {
  getCongressRecent, getCongressTopBought, getCongressTopSold,
  getInsiderLarge, getInsiderClusters, getGuruPortfolios,
  getGovContracts,
  getFedIndicators, getYieldCurve, getRecessionSignals, getFedCalendar,
  getFearGreed, getWsbTrending, getTrendingTickers,
  getNewsHeadlines, getMaterialEvents,
  getMostShorted, getSqueezeCandidates,
  getETFInflows, getETFOutflows, getSectorRotation,
  getLobbyingTopSpenders, getTopInnovators,
  getUnusualFlights,
  getUnusualOptions, getPutCallRatio,
  getMostActiveDarkPool,
  getWhaleLargeMoves, getWhaleExchangeFlows,
  getTrendingSearches, getCramerPicks, getRecentSplits,
} from '@/lib/api'
import {
  isAltDataEmpty, saveAltSnapshot, loadAltSnapshot,
} from '@/lib/altDataCache'
import { isSnapshotFresh } from '@/lib/snapshotCache'
import type {
  CongressTrade, InsiderTrade, InsiderCluster,
  GovContract, FedIndicator, YieldCurvePoint,
  FomcMeeting, WsbPost, TrendingTicker, NewsHeadline, MaterialEvent,
  ShortedStock, SqueezeCandidate, ETFFlow, SectorRotation,
  LobbyingSpender, TopInnovator, UnusualFlightPattern,
  UnusualOptionsTrade, PutCallRatio, DarkPoolTicker,
  WhaleMoveEntry, ExchangeFlows,
  CramerPick, StockSplit,
} from '@/lib/types'

/* ── Backend-truth field overrides ──────────────────────────────────────────
   Several interfaces in lib/types.ts drifted from what the backend actually
   returns. Rather than fabricate the old fields, the store types below extend
   the shared types with the REAL backend field names (all optional/nullable —
   every API field can be null). Readers must use these fields. */

/** ETF "flows" are really price-change proxies: backend sends price_change_pct. */
export type EtfFlowEntry = ETFFlow & { price_change_pct?: number | null; note?: string }
/** Sector rotation aggregates price changes: avg_change_pct, not total_flow_m. */
export type SectorRotationEntry = SectorRotation & { avg_change_pct?: number | null; note?: string }
/** Reddit trending sends avg_score (plus totals), not score. */
export type TrendingTickerEntry = TrendingTicker & {
  avg_score?: number | null; total_score?: number | null; total_comments?: number | null
}
/** Put/call ratio sends volume_pcr / oi_pcr, not equity_pcr / index_pcr. */
export type PutCallRatioEntry = PutCallRatio & { volume_pcr?: number | null; oi_pcr?: number | null }
/** Gov contracts send company_name, not company. */
export type GovContractEntry = GovContract & { company_name?: string | null }
/** Splits send a single date field, not ex_date/announced. */
export type StockSplitEntry = StockSplit & { date?: string | null }
/** Squeeze candidates send float_shares (raw share count), not float_m. */
export type SqueezeCandidateEntry = SqueezeCandidate & { float_shares?: number | null }
/** 8-K events send type/summary/title and have NO ticker. */
export type MaterialEventEntry = MaterialEvent & {
  type?: string | null; summary?: string | null; title?: string | null
}
/** Real recession-signals payload: recession_risk / risk_score (0-7) + string signals. */
export interface RecessionSignalsEntry {
  recession_risk?: string | null
  risk_score?: number | null
  signals?: string[] | null
  yield_curve_inverted?: boolean | null
  yield_spread?: number | null
}
/** Normalised guru portfolio (backend sends a dict keyed by manager; 13F
    holdings have issuer names, NOT tickers). */
export interface GuruPortfolioEntry {
  manager: string
  fund_name: string
  total_value: number | null
  top_holdings: Array<{ name: string; value: number | null; portfolio_pct: number | null }>
}

interface AltDataState {
  loading: boolean
  error: string | null
  lastFetched: Date | null
  /** Per-source fetch failures from the last load — so an empty page is
      debuggable instead of silently blank. */
  failures: Array<{ source: string; reason: string }>
  totalSources: number
  /** True when the displayed panels are the last-good cached payload because
      the most recent fetch came back entirely empty (sources down). */
  stale: boolean
  /** Epoch ms the currently-displayed data was actually fetched (live or
      cached) — drives the "showing cached data from X" badge. */
  dataAsOf: number | null

  // Political
  congressTrades: CongressTrade[]
  congressTopBought: Array<{ ticker: string; count: number }>
  congressTopSold: Array<{ ticker: string; count: number }>
  govContracts: GovContractEntry[]

  // Insider
  insiderLarge: InsiderTrade[]
  insiderClusters: InsiderCluster[]
  guruPortfolios: GuruPortfolioEntry[]

  // Macro
  fedIndicators: FedIndicator[]
  yieldCurve: YieldCurvePoint[]
  recessionSignals: RecessionSignalsEntry | null
  fomcMeetings: FomcMeeting[]

  // Sentiment
  fearGreedValue: number | null
  fearGreedLabel: string
  wsbPosts: WsbPost[]
  trendingTickers: TrendingTickerEntry[]

  // News
  headlines: NewsHeadline[]
  materialEvents: MaterialEventEntry[]

  // Short Interest
  mostShorted: ShortedStock[]
  squeezeCandidates: SqueezeCandidateEntry[]

  // ETF Flows
  etfInflows: EtfFlowEntry[]
  etfOutflows: EtfFlowEntry[]
  sectorRotation: SectorRotationEntry[]

  // Lobbying
  lobbyingSpenders: LobbyingSpender[]

  // Patents
  topInnovators: TopInnovator[]

  // Corporate Flights
  unusualFlights: UnusualFlightPattern[]

  // Options Flow
  unusualOptions: UnusualOptionsTrade[]
  putCallRatio: PutCallRatioEntry | null

  // Dark Pool
  darkPoolMostActive: DarkPoolTicker[]

  // Whale Alerts
  whaleMoves: WhaleMoveEntry[]
  exchangeFlows: ExchangeFlows | null

  // QuiverQuant-style
  googleTrending: string[]
  cramerPicks: CramerPick[]
  recentSplits: StockSplitEntry[]

  fetchAll: () => Promise<void>
}

/** Source names aligned 1:1 with the Promise.allSettled order in fetchAll, so
    a rejected fetch can be named in the failure banner. MUST stay in sync with
    that array — the pinning test asserts the length. */
export const ALT_DATA_SOURCE_LABELS = [
  'Congress trades', 'Congress top bought', 'Congress top sold', 'Gov contracts',
  'Insider (large)', 'Insider clusters', 'Guru portfolios', 'Fed indicators',
  'Yield curve', 'Recession signals', 'Fed calendar', 'Fear & Greed',
  'WSB trending', 'Trending tickers', 'News headlines', 'Material events',
  'Most shorted', 'Squeeze candidates', 'ETF inflows', 'ETF outflows',
  'Sector rotation', 'Lobbying spenders', 'Top innovators', 'Unusual flights',
  'Unusual options', 'Put/Call ratio', 'Dark pool', 'Whale moves',
  'Exchange flows', 'Google Trends', 'Cramer picks', 'Recent splits',
] as const

/** Pull a human-readable reason out of an axios error / thrown value, capped. */
export function extractRejectReason(reason: unknown): string {
  const r = reason as { response?: { data?: { detail?: string } }; message?: string } | undefined
  return String(r?.response?.data?.detail ?? r?.message ?? reason).slice(0, 200)
}

// Rehydrate the last-good payload synchronously at store creation so a page
// reload shows real (if stale) data immediately while the fresh fetch runs,
// instead of flashing 32 empty panels.
// Bound rehydration to 24h — a days-old alt-data cache shown as if live is
// worse than an empty page that immediately refetches.
const ALT_SNAPSHOT_MAX_AGE_MS = 24 * 60 * 60 * 1000
const _snapshotRaw = loadAltSnapshot()
const _snapshot = _snapshotRaw && isSnapshotFresh(_snapshotRaw.savedAt, Date.now(), ALT_SNAPSHOT_MAX_AGE_MS)
  ? _snapshotRaw
  : null
const _hydrated = (_snapshot?.data ?? {}) as Partial<AltDataState>

export const useAltData = create<AltDataState>()((set) => ({
  loading: false,
  error: null,
  lastFetched: null,
  failures: [],
  totalSources: 0,
  stale: false,
  dataAsOf: _snapshot?.savedAt ?? null,

  congressTrades: [],
  congressTopBought: [],
  congressTopSold: [],
  govContracts: [],

  insiderLarge: [],
  insiderClusters: [],
  guruPortfolios: [],

  fedIndicators: [],
  yieldCurve: [],
  recessionSignals: null,
  fomcMeetings: [],

  fearGreedValue: null,
  fearGreedLabel: '',
  wsbPosts: [],
  trendingTickers: [],

  headlines: [],
  materialEvents: [],

  mostShorted: [],
  squeezeCandidates: [],

  etfInflows: [],
  etfOutflows: [],
  sectorRotation: [],

  lobbyingSpenders: [],

  topInnovators: [],

  unusualFlights: [],

  unusualOptions: [],
  putCallRatio: null,

  darkPoolMostActive: [],

  whaleMoves: [],
  exchangeFlows: null,

  googleTrending: [],
  cramerPicks: [],
  recentSplits: [],

  // Cached last-good panels override the empty defaults above (if present).
  ..._hydrated,

  fetchAll: async () => {
    set({ loading: true, error: null })
    try {
      const results = await Promise.allSettled([
        getCongressRecent(30),           // 0
        getCongressTopBought(30, 10),    // 1
        getCongressTopSold(30, 10),      // 2
        getGovContracts(30),             // 3
        getInsiderLarge(100_000, 30),    // 4
        getInsiderClusters(30),          // 5
        getGuruPortfolios(),             // 6
        getFedIndicators(),              // 7
        getYieldCurve(),                 // 8
        getRecessionSignals(),           // 9
        getFedCalendar(),                // 10
        getFearGreed(),                  // 11
        getWsbTrending(15),              // 12
        getTrendingTickers(15),          // 13
        getNewsHeadlines(20),            // 14
        getMaterialEvents(7),            // 15
        getMostShorted(20),              // 16
        getSqueezeCandidates(),          // 17
        getETFInflows(7),                // 18
        getETFOutflows(7),               // 19
        getSectorRotation(),             // 20
        getLobbyingTopSpenders(15),      // 21
        getTopInnovators(15),            // 22
        getUnusualFlights(),             // 23
        getUnusualOptions(50_000),       // 24
        getPutCallRatio(),               // 25
        getMostActiveDarkPool(),         // 26
        getWhaleLargeMoves(1_000_000),   // 27
        getWhaleExchangeFlows(),         // 28
        getTrendingSearches(),           // 29
        getCramerPicks(30),              // 30
        getRecentSplits(180),            // 31
      ])

      const val = <T,>(idx: number, fallback: T): T => {
        const r = results[idx]
        return r.status === 'fulfilled' ? r.value : fallback
      }

      const failures = results.flatMap((r, i) =>
        r.status === 'rejected'
          ? [{ source: ALT_DATA_SOURCE_LABELS[i] ?? `source ${i}`, reason: extractRejectReason(r.reason) }]
          : []
      )

      // Backend /institutional/gurus returns {gurus: {manager: {...}}} — a
      // DICT, not an array. Normalise to a list; 13F holdings carry issuer
      // names (no tickers) so portfolio % is derived from value/total_value.
      const guruRaw = val<{ gurus?: Record<string, {
        fund?: string; total_value?: number; error?: string
        top_10?: Array<{ name?: string; value?: number }>
      }> } | null>(6, null)
      const guruList: GuruPortfolioEntry[] = Object.entries(guruRaw?.gurus ?? {})
        .filter(([, g]) => g && !g.error)
        .map(([manager, g]) => {
          const totalValue = typeof g.total_value === 'number' && g.total_value > 0 ? g.total_value : null
          return {
            manager,
            fund_name: typeof g.fund === 'string' ? g.fund : '',
            total_value: totalValue,
            top_holdings: (Array.isArray(g.top_10) ? g.top_10 : []).map((h) => ({
              name: typeof h?.name === 'string' ? h.name : '',
              value: typeof h?.value === 'number' ? h.value : null,
              portfolio_pct: typeof h?.value === 'number' && totalValue != null
                ? (h.value / totalValue) * 100
                : null,
            })),
          }
        })

      // Yield curve: backend now sends a list of {maturity, rate}; guard the
      // shape so a dict payload (older server) can never .map-crash the page.
      const yieldCurveRaw = val(8, { yield_curve: [] as YieldCurvePoint[] }).yield_curve
      // Put/call ratio: backend returns {message} when the chain fetch fails —
      // treat anything without a numeric total_pcr as "no data", not a payload.
      const pcrRaw = val<PutCallRatioEntry | { message?: string } | null>(25, null)

      // Panel-data slice only (no meta) — what we persist + emptiness-check.
      const derived: Partial<AltDataState> = {
        congressTrades: val(0, { trades: [] }).trades ?? [],
        congressTopBought: val(1, { top_bought: [] }).top_bought ?? [],
        congressTopSold: val(2, { top_sold: [] }).top_sold ?? [],
        govContracts: val(3, { awards: [] }).awards ?? [],
        insiderLarge: val(4, []),
        insiderClusters: val(5, []),
        guruPortfolios: guruList,
        fedIndicators: val(7, { indicators: [] }).indicators ?? [],
        yieldCurve: Array.isArray(yieldCurveRaw) ? yieldCurveRaw : [],
        recessionSignals: val(9, null),
        fomcMeetings: val(10, { meetings: [] }).meetings ?? [],
        fearGreedValue: val(11, { value: null } as { value: number | null; classification?: string }).value ?? null,
        fearGreedLabel: val(11, { classification: '' } as { value?: number; classification: string }).classification ?? '',
        wsbPosts: val(12, { posts: [] }).posts ?? [],
        trendingTickers: val(13, { trending: [] }).trending ?? [],
        headlines: val(14, { headlines: [] }).headlines ?? [],
        materialEvents: val(15, { events: [] }).events ?? [],
        mostShorted: val(16, { stocks: [] }).stocks ?? [],
        squeezeCandidates: val(17, { candidates: [] }).candidates ?? [],
        etfInflows: val(18, { inflows: [] }).inflows ?? [],
        etfOutflows: val(19, { outflows: [] }).outflows ?? [],
        sectorRotation: val(20, { sectors: [] }).sectors ?? [],
        lobbyingSpenders: val(21, { spenders: [] }).spenders ?? [],
        topInnovators: val(22, { innovators: [] }).innovators ?? [],
        unusualFlights: val(23, { patterns: [] }).patterns ?? [],
        unusualOptions: val(24, { trades: [] }).trades ?? [],
        putCallRatio: pcrRaw && typeof (pcrRaw as PutCallRatioEntry).total_pcr === 'number'
          ? (pcrRaw as PutCallRatioEntry)
          : null,
        darkPoolMostActive: val(26, { tickers: [] }).tickers ?? [],
        whaleMoves: val(27, { moves: [] }).moves ?? [],
        exchangeFlows: val(28, null),
        googleTrending: val(29, { trending: [] }).trending ?? [],
        cramerPicks: val(30, { picks: [] }).picks ?? [],
        recentSplits: val(31, { splits: [] }).splits ?? [],
      }

      // Last-good cache: if every panel came back empty *and* sources failed,
      // the fetch didn't really succeed — keep showing the prior (cached) data
      // marked stale rather than blanking the page. Otherwise commit the fresh
      // payload and persist it as the new last-good snapshot.
      if (isAltDataEmpty(derived as Record<string, unknown>) && failures.length > 0) {
        set({ loading: false, failures, totalSources: results.length, stale: true })
      } else {
        const now = Date.now()
        saveAltSnapshot(derived as Record<string, unknown>, now)
        set({
          loading: false,
          lastFetched: new Date(),
          failures,
          totalSources: results.length,
          stale: false,
          dataAsOf: now,
          ...derived,
        })
      }
    } catch (e) {
      set({ error: String(e), loading: false })
    }
  },
}))
